import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTabDraft } from "@/components/ui/box-draft";
import type { Result } from "@/components/ui/box.schema";
import { ApiError } from "@/lib/api/client";
import { checkCommit } from "@/lib/api/commitSeams";
import { quizzesApi, type QuizAnswers, type QuizHandIn, type QuizRead, type QuizSave, type QuizSaveAck } from "@/lib/api/quizzes";
import { invalidateAssignment } from "../invalidate-assignment";

type Pending = { kind: "save"; body: QuizSave; ack?: QuizSaveAck } | { kind: "hand-in"; key: string; revision: number };
export const sameQuizAnswers = (a: QuizAnswers, b: QuizAnswers) => JSON.stringify(Object.entries(a).sort(([x], [y]) => x.localeCompare(y))) === JSON.stringify(Object.entries(b).sort(([x], [y]) => x.localeCompare(y)));
const definite = (error: unknown): error is ApiError => error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 408;
const savedWithoutRead = (ack: QuizSaveAck): Result => ({ state: "confirmed", at: ack.saved_at, effects: ["The original answer save is confirmed."], remains: ["Read the quiz again before continuing. Your writing remains in this tab."] });

/** The kit retains exact unsent answers and operation identity in this login's tab. */
export function useQuizAnswers({ scope, runId, assignmentId, courseId, allowed }: { scope: string; runId: string; assignmentId: string; courseId: string; allowed: boolean }) {
  const client = useQueryClient(), queryKey = ["quiz-run", scope, runId];
  const read = useQuery({ queryKey, queryFn: () => quizzesApi.read(runId), retry: false, refetchOnWindowFocus: false });
  const data = read.data;
  const belongs = data?.run.id === runId && data.run.assignment_id === assignmentId && data.run.course_id === courseId;
  const tab = useTabDraft<QuizAnswers, Pending>({ box: "quiz-answers", record: runId, version: data ? String(data.revision) : null, initial: data?.answers ?? {} });
  const [state, setState] = useState<{ busy: boolean; pause: boolean; result: Result }>({ busy: false, pause: false, result: { state: "none" } });
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const update = (patch: Partial<typeof state>) => { if (live.current) setState(value => ({ ...value, ...patch })); };
  const ready = Boolean(belongs && !read.isError && tab.settled);
  const editable = ready && allowed && !data?.run.submitted_id && !data?.run.erased_at;
  const current = useRef({ tab, data, editable, state }); current.current = { tab, data, editable, state };
  const flight = useRef<Promise<QuizSaveAck | null> | null>(null);
  const handInFlight = useRef<Promise<Result> | null>(null);
  const refused = (sentence: string): Result => ({ state: "refused", cap: "Answers kept", sentence, draft: "kept" });
  const stop = (sentence: string) => { const result = refused(sentence); update({ result, pause: true }); return result; };
  const acknowledge = async (send: Extract<Pending, { kind: "save" }>, ack: QuizSaveAck, end: (version?: string) => void): Promise<Result> => {
    // A recovered save can beat the first read after reload. Keep that read
    // alive: its paper is still needed before acknowledging the kept writing.
    if (!client.getQueryData<QuizRead>(queryKey)) {
      tab.setPending({ ...send, ack });
      const result = savedWithoutRead(ack); update({ result, pause: true }); return result;
    }
    await client.cancelQueries({ queryKey });
    client.setQueryData<QuizRead>(queryKey, value => value && value.revision <= ack.revision ? { ...value, answers: send.body.answers, revision: ack.revision, saved_at: ack.saved_at } : value);
    end(String(ack.revision)); update({ result: { state: "none" }, pause: false });
    return { state: "none" };
  };
  const acknowledgeRef = useRef(acknowledge); acknowledgeRef.current = acknowledge;
  const { pending: pendingSave, sending } = tab;
  useEffect(() => {
    if (belongs && data && pendingSave?.kind === "save" && pendingSave.ack) void acknowledgeRef.current(pendingSave, pendingSave.ack, sending(true));
  }, [belongs, data, pendingSave, sending]);
  const confirmed = async (receipt: QuizHandIn, end: (version?: string) => void): Promise<Result> => {
    if (receipt.run_id !== runId || receipt.assignment_id !== assignmentId) throw new Error("Quiz receipt identity differs");
    await client.cancelQueries({ queryKey });
    client.setQueryData<QuizRead>(queryKey, value => value ? { ...value, run: { ...value.run, submitted_id: receipt.id } } : value);
    end(String(receipt.source_draft_revision));
    const result: Result = { state: "confirmed", at: receipt.submitted_at, effects: ["Your saved answers were handed in once. This receipt keeps their original quiz version."], remains: [] };
    update({ result, pause: false });
    void client.invalidateQueries({ queryKey });
    void client.invalidateQueries({ queryKey: ["quiz-runs", scope, assignmentId] });
    void invalidateAssignment(client, courseId, assignmentId);
    return result;
  };
  const unknown = (send: Pending): Result => ({ state: "unknown", requestId: send.kind === "save" ? send.body.operation_id : send.key,
    cap: send.kind === "save" ? "Save not confirmed" : "Hand-in not confirmed", sentence: "Your answers are kept. Check the original request before sending a replacement.",
    check: { verb: send.kind === "save" ? "Check saved answers" : "Check hand-in", note: "Reads the original result without sending your answers again.", run: async () => {
      if (current.current.state.busy) return unknown(send);
      update({ busy: true }); const end = tab.sending(true);
      try {
        if (send.kind === "save") {
          const ack = await quizzesApi.checkSave(runId, send.body);
          if (ack.acknowledged) return await acknowledge(send, ack, end);
        } else {
          const checked = await checkCommit<{ value: QuizHandIn }>(send.key);
          if (checked.state === "confirmed") return await confirmed(checked.receipt.result.value, end);
          if (checked.state !== "not_committed") return unknown(send);
        }
        tab.setPending(null); return stop("The original request was not recorded. Your answers are kept; review them before trying again.");
      } catch { const result = unknown(send); update({ result, pause: true }); return result; }
      finally { update({ busy: false }); }
    } } });
  const save = async (reviewed = false): Promise<QuizSaveAck | null> => {
    if (flight.current) return flight.current;
    const now = current.current;
    if (!now.editable || !now.data || now.tab.pending || now.tab.conflict || now.state.pause && !reviewed) return null;
    if (sameQuizAnswers(now.tab.value, now.data.answers)) return { revision: now.data.revision, saved_at: now.data.saved_at ?? now.data.run.started_at };
    const send: Extract<Pending, { kind: "save" }> = { kind: "save", body: { operation_id: crypto.randomUUID(), expected_revision: now.data.revision, answers: structuredClone(now.tab.value) } };
    now.tab.setPending(send); const end = now.tab.sending(); update({ busy: true, result: { state: "none" } });
    const work = (async () => {
      try { const ack = await quizzesApi.save(runId, send.body); await acknowledge(send, ack, end); return ack; }
      catch (error) {
        if (definite(error)) { now.tab.setPending(null); stop(error.message); if (error.status === 409) void read.refetch(); }
        else update({ result: unknown(send), pause: true });
        return null;
      } finally { flight.current = null; update({ busy: false }); }
    })();
    flight.current = work; return work;
  };
  const saveRef = useRef(save); saveRef.current = save;
  const dirty = Boolean(data && !sameQuizAnswers(tab.value, data.answers));
  const firstDirty = useRef<number | null>(null), serialized = JSON.stringify(tab.value);
  useEffect(() => {
    if (!editable || !dirty || tab.pending || tab.conflict || state.pause) { if (!dirty) firstDirty.current = null; return; }
    firstDirty.current ??= Date.now();
    const timer = setTimeout(() => { firstDirty.current = null; void saveRef.current(); }, Math.max(0, Math.min(600, 8000 - (Date.now() - firstDirty.current!))));
    return () => clearTimeout(timer);
  }, [serialized, editable, dirty, tab.pending, tab.conflict, state.pause]);
  const performHandIn = async (key: string): Promise<Result> => {
    const candidate = structuredClone(current.current.tab.value);
    const ack = await save(true), latest = client.getQueryData<QuizRead>(queryKey);
    if (!ack || !current.current.editable || current.current.tab.pending?.kind === "hand-in" || current.current.tab.conflict
      || !latest || latest.revision !== ack.revision || !sameQuizAnswers(candidate, latest.answers) || !sameQuizAnswers(candidate, current.current.tab.value))
      return current.current.tab.pending ? unknown(current.current.tab.pending) : stop("The saved answers do not yet match your current writing. Review them before handing in.");
    const send: Pending = { kind: "hand-in", key, revision: ack.revision };
    tab.set(candidate); tab.setPending(send); const end = tab.sending(); update({ busy: true });
    try { return await confirmed(await quizzesApi.handIn(runId, ack.revision, key), end); }
    catch (error) {
      if (definite(error)) { tab.setPending(null); void read.refetch(); return stop(error.message); }
      const result = unknown(send); update({ result, pause: true }); return result;
    } finally { update({ busy: false }); }
  };
  const handIn = (key: string): Promise<Result> => {
    if (handInFlight.current) return handInFlight.current;
    const work = performHandIn(key).finally(() => { handInFlight.current = null; });
    handInFlight.current = work;
    return work;
  };
  return { data, read, tab, ready, editable, dirty, busy: state.busy, save, handIn,
    result: tab.pending && !state.busy && state.result.state === "none" ? tab.pending.kind === "save" && tab.pending.ack ? savedWithoutRead(tab.pending.ack) : unknown(tab.pending) : state.result,
    change: (answers: QuizAnswers) => { tab.set(answers); if (!tab.pending) update({ result: { state: "none" }, pause: false }); },
    keepMine: () => { tab.keepMine(); update({ result: { state: "none" }, pause: false }); },
    loadSaved: () => { tab.loadNewer(); update({ result: { state: "none" }, pause: false }); },
  };
}
